/**
 * Text layout shared by the canvas renderer and the content-extent helper
 * (measureContent). Both call layoutText(), so wrap points, line heights and
 * line counts can't disagree.
 *
 * Replaces two NS implementations that had drifted apart:
 *   - canvas-renderer.js drawWrappedText (line height 1.2 × font height, CSS widths)
 *   - page-splitter.js estimateLines (line height 1.4 × font height, 0.55 × height per char)
 * Zebra's own rules are used instead: a ^FB line is the font height plus the
 * ^FB line spacing, "\&" forces a break, and text beyond the ^FB line limit
 * overprints the last line.
 */

import type { FontSpec, TextElement } from './document-model';
import { estimateTextWidthDots, lineBoxHeightDots } from './font-mapper';

/** Width of a string in dots for a given font. */
export type MeasureText = (text: string, font: FontSpec) => number;

export interface LaidOutLine {
  text: string;
  /** Left edge in dots, after justification and hanging indent. */
  x: number;
  /** Top edge in dots. */
  y: number;
  width: number;
}

export interface TextLayout {
  lines: LaidOutLine[];
  lineHeight: number;
  /** Lines the text needed before the ^FB line limit was applied. */
  neededLines: number;
  /** True when text didn't fit in the ^FB line limit and overprints the last line. */
  overflowed: boolean;
  /** Bounding box in dots (unrotated, relative to the label). */
  width: number;
  height: number;
}

/** Height of one line in dots: font height plus ^FB line spacing. */
export function lineHeightDots(el: Pick<TextElement, 'font' | 'block'>): number {
  return lineBoxHeightDots(el.font) + (el.block?.lineSpacing ?? 0);
}

function breakWord(word: string, maxWidth: number, font: FontSpec, measure: MeasureText): string[] {
  const parts: string[] = [];
  let cur = '';
  for (const ch of word) {
    if (cur && measure(cur + ch, font) > maxWidth) {
      parts.push(cur);
      cur = ch;
    } else {
      cur += ch;
    }
  }
  if (cur) parts.push(cur);
  return parts;
}

/** Word-wrap one paragraph to maxWidth; words wider than the block are broken by character. */
export function wrapParagraph(text: string, maxWidth: number, font: FontSpec, measure: MeasureText): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let cur = '';
  for (const word of words) {
    const test = cur ? `${cur} ${word}` : word;
    if (measure(test, font) <= maxWidth) {
      cur = test;
      continue;
    }
    if (cur) lines.push(cur);
    if (measure(word, font) > maxWidth) {
      const pieces = breakWord(word, maxWidth, font, measure);
      cur = pieces.pop() ?? '';
      lines.push(...pieces);
    } else {
      cur = word;
    }
  }
  lines.push(cur);
  return lines;
}

/**
 * Lay out a text field in dots. Without ^FB the field is one line that may run
 * past the label edge (the printer clips it). With ^FB it wraps to the block.
 */
export function layoutText(el: TextElement, measure: MeasureText = estimateTextWidthDots): TextLayout {
  const lineHeight = lineHeightDots(el);
  const block = el.block;

  if (!block) {
    const width = measure(el.text, el.font);
    return {
      lines: [{ text: el.text, x: el.x, y: el.y, width }],
      lineHeight,
      neededLines: 1,
      overflowed: false,
      width,
      height: lineHeightDots(el),
    };
  }

  const maxWidth = block.maxWidth > 0 ? block.maxWidth : Number.POSITIVE_INFINITY;
  const wrapped: string[] = [];
  for (const paragraph of el.text.split('\\&')) {
    wrapped.push(...wrapParagraph(paragraph, maxWidth, el.font, measure));
  }

  const maxLines = Math.max(1, block.maxLines);
  const lines: LaidOutLine[] = wrapped.map((text, i) => {
    // Lines past the limit are printed on top of the last allowed line.
    const row = Math.min(i, maxLines - 1);
    const width = measure(text, el.font);
    const indent = i > 0 ? block.hangingIndent : 0;
    const avail = Number.isFinite(maxWidth) ? maxWidth : width;
    let x = el.x + indent;
    if (block.align === 'C') x = el.x + (avail - width) / 2;
    else if (block.align === 'R') x = el.x + avail - width;
    return { text, x, y: el.y + row * lineHeight, width };
  });

  const usedRows = Math.min(wrapped.length, maxLines);
  const contentWidth = Math.max(...lines.map((l) => l.x - el.x + l.width));
  return {
    lines,
    lineHeight,
    neededLines: wrapped.length,
    overflowed: wrapped.length > maxLines,
    width: Number.isFinite(maxWidth) ? maxWidth : contentWidth,
    // The printer reserves the full block height (maxLines), not just the used rows.
    height: Math.max(usedRows, maxLines) * lineHeight - (block.lineSpacing ?? 0),
  };
}
